import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoListDropdown } from "@redotech/arbiter/list/redo-list-dropdown";
import { RedoSpinner } from "@redotech/arbiter/loading/redo-spinner";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { memo, ReactNode, useEffect, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamAvatarLabel } from "./team-avatar-label";

interface TeamOption {
  _id: string;
  name: string;
  logoUrl?: string;
}

export interface TeamSearchBoxProps {
  value: string;
  setValue: (value: string) => void;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  onFocus?: () => void;
  onBlur?: () => void;
  /** When true, a confirmed selection renders as a card instead of raw input text. */
  showSelectedCard?: boolean;
  /** Rendered beside the input or selected-store card, vertically aligned with that control. */
  trailing?: ReactNode;
}

export const TeamSearchBox = memo(function TeamSearchBox({
  value,
  setValue,
  label = "Team",
  placeholder = "Search by team name or enter team ID...",
  size = "sm",
  onFocus,
  onBlur,
  showSelectedCard = false,
  trailing,
}: TeamSearchBoxProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (value === "" && searchQuery !== "") {
      setSearchQuery("");
      setShowDropdown(false);
      setTeamOptions([]);
      setSelectedTeam(null);
    }
  }, [value, searchQuery]);

  // Restore display text when value is set externally (e.g. from URL params)
  useEffect(() => {
    async function restoreTeamName() {
      if (!value || searchQuery) {
        return;
      }
      setSearchQuery(value);
      setIsSearching(true);
      try {
        const result = await rpcClient.searchTeams({ query: value, limit: 1 });
        const match = result.teams.find((t: TeamOption) => t._id === value);
        setSearchQuery(match ? `${match.name} (${match._id})` : value);
        setSelectedTeam(match ?? { _id: value, name: value });
      } catch {
        setSearchQuery(value);
      } finally {
        setIsSearching(false);
      }
    }
    void restoreTeamName();
    // Only run on mount when value is pre-populated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTeams = useHandler(async (query: string) => {
    if (query.trim().length < 2) {
      setTeamOptions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const result = await rpcClient.searchTeams({
        query: query.trim(),
        limit: 10,
      });
      setTeamOptions(result.teams);
      setShowDropdown(true);
    } catch (error) {
      console.error("Team search failed:", error);
      setTeamOptions([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  });

  const handleTeamSelect = useHandler((team: TeamOption) => {
    setValue(team._id);
    setSearchQuery(`${team.name} (${team._id})`);
    setSelectedTeam(team);
    setShowDropdown(false);
    setTeamOptions([]);
  });

  const handleClearSelection = useHandler(() => {
    setValue("");
    setSearchQuery("");
    setSelectedTeam(null);
    setShowDropdown(false);
    setTeamOptions([]);
  });

  const handleInputChange = useHandler((inputValue: string) => {
    setSearchQuery(inputValue);
    setSelectedTeam(null);

    const parenthesesMatch = inputValue.match(/\(([^)]+)\)/);

    if (parenthesesMatch && parenthesesMatch[1]) {
      setValue(parenthesesMatch[1].trim());
    } else if (inputValue.trim()) {
      setValue(inputValue.trim());
    } else {
      setValue("");
    }

    // Debounce search — only user typing triggers searches
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (inputValue.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(
        () => void searchTeams(inputValue),
        300,
      );
    } else {
      setTeamOptions([]);
      setShowDropdown(false);
    }
  });

  const teamListItems: RedoListItem<TeamOption>[] = teamOptions.map((team) => ({
    id: team._id,
    type: "text",
    text: team.name,
    supportingText: team._id,
    value: team,
    leadingItem: {
      type: "avatar",
      imageUrl: team.logoUrl,
      alt: team.name,
      name: team.name,
    },
  }));

  if (showSelectedCard && selectedTeam && value) {
    return (
      <Flex dir="column" gap="xs" w="full">
        {label && (
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {label}
          </Text>
        )}
        <Flex align="center" dir="row" gap="sm" w="full">
          <Flex
            align="center"
            bgColor="secondary"
            borderColor="brand"
            borderRadius="lg"
            borderStyle="solid"
            borderWidth="1px"
            gap="md"
            grow={1}
            justify="space-between"
            minW="none"
            p="sm"
          >
            <TeamAvatarLabel
              logoUrl={selectedTeam.logoUrl}
              name={selectedTeam.name}
              subtitle={selectedTeam._id}
            />
            <RedoButton
              hierarchy="tertiary"
              onClick={handleClearSelection}
              size="sm"
              text="Change"
            />
          </Flex>
          {trailing && <Flex shrink={0}>{trailing}</Flex>}
        </Flex>
      </Flex>
    );
  }

  const searchField = (
    <Flex dir="column" ref={setDropdownAnchor} w="full">
      <RedoTextInput
        label={label}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        setValue={handleInputChange}
        size={size}
        value={searchQuery}
      />
    </Flex>
  );

  return (
    <Flex dir="column" gap="none" w="full">
      {trailing ? (
        <Flex align="flex-end" dir="row" gap="sm" w="full">
          <Flex grow={1} minW="none" w="full">
            {searchField}
          </Flex>
          <Flex shrink={0}>{trailing}</Flex>
        </Flex>
      ) : (
        searchField
      )}
      {isSearching && (
        <Flex align="center" pt="sm">
          <RedoSpinner size="xs" />
        </Flex>
      )}
      <RedoListDropdown
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={showDropdown && teamOptions.length > 0}
        fitToAnchor
        items={teamListItems}
        itemSelected={({ item }) => {
          handleTeamSelect(item.value);
        }}
        preventCloseOnAnchorClick
        selectionVariant="none"
        setDropdownOpen={setShowDropdown}
        size="xs"
      />
      {searchQuery.trim() !== "" &&
        !value &&
        !showDropdown &&
        teamOptions.length === 0 &&
        !isSearching && (
          <Text fontSize="xs" textColor="secondary">
            No teams found matching your search.
          </Text>
        )}
    </Flex>
  );
});
